import { apiFetch } from './api';
import React, { useEffect, useState, useCallback } from 'react';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d)) return dateStr;
  return d.toLocaleDateString('it-IT', { day: '2-digit', month: '2-digit', year: 'numeric' });
}

function ContattiPrenotazioneNuovo({ onIniziativaSelect }) {
  const [iniziative, setIniziative] = useState([]);
  const [filteredIniziative, setFilteredIniziative] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedId, setSelectedId] = useState(null);

  // Filtri
  const [searchTitolo, setSearchTitolo] = useState('');
  const [searchLuogo, setSearchLuogo] = useState('');
  const [searchCategoria, setSearchCategoria] = useState('');

  // Ordinamento
  const [sortField, setSortField] = useState('dataDa');
  const [sortDirection, setSortDirection] = useState('asc');
  const [processingSelection, setProcessingSelection] = useState(false);

  useEffect(() => {
    async function fetchIniziative() {
      setLoading(true);
      setError(null);
      try {
        const response = await apiFetch('/api/Iniziative/mw-lista');
        if (response.ok) {
          const data = await response.json();
          const arr = Array.isArray(data) ? data : [];
          setIniziative(arr);
          setFilteredIniziative(arr);
          console.log(`[MIDDLEWARE] Caricate ${arr.length} iniziative`);
        } else {
          setError(`Errore caricamento: ${response.status}`);
          setIniziative([]);
          setFilteredIniziative([]);
        }
      } catch (err) {
        console.error('[MIDDLEWARE] Errore:', err);
        setError('Errore di connessione al server');
        setIniziative([]);
        setFilteredIniziative([]);
      }
      setLoading(false);
    }
    fetchIniziative();
  }, []);

  const handleSort = useCallback((field) => {
    setProcessingSelection(true);
    setSortField(prev => {
      setSortDirection(prevDir => (prev === field ? (prevDir === 'asc' ? 'desc' : 'asc') : 'asc'));
      return field;
    });
  }, []);

  const getSortIcon = (field) => {
    if (sortField !== field) return <i className="fas fa-sort text-muted ms-1"></i>;
    return sortDirection === 'asc'
      ? <i className="fas fa-sort-up text-primary ms-1"></i>
      : <i className="fas fa-sort-down text-primary ms-1"></i>;
  };

  const applyFilters = useCallback(() => {
    setProcessingSelection(true);
    setTimeout(() => {
      let filtered = [...iniziative];

      if (searchTitolo.trim())
        filtered = filtered.filter(i => (i.titolo || '').toLowerCase().includes(searchTitolo.toLowerCase()));

      if (searchLuogo.trim())
        filtered = filtered.filter(i => (i.luogo || '').toLowerCase().includes(searchLuogo.toLowerCase()));

      if (searchCategoria.trim())
        filtered = filtered.filter(i => (i.categoria || '').toLowerCase().includes(searchCategoria.toLowerCase()));

      if (sortField) {
        filtered.sort((a, b) => {
          let va = a[sortField] ?? '';
          let vb = b[sortField] ?? '';
          va = String(va).toLowerCase();
          vb = String(vb).toLowerCase();
          if (va < vb) return sortDirection === 'asc' ? -1 : 1;
          if (va > vb) return sortDirection === 'asc' ? 1 : -1;
          return 0;
        });
      }

      setFilteredIniziative(filtered);
      setProcessingSelection(false);
    }, 0);
  }, [iniziative, searchTitolo, searchLuogo, searchCategoria, sortField, sortDirection]);

  useEffect(() => {
    applyFilters();
  }, [applyFilters]);

  const handleSelect = useCallback((id, iniziativa) => {
    setSelectedId(id);
    if (onIniziativaSelect) onIniziativaSelect(id, iniziativa);
  }, [onIniziativaSelect]);

  const getStatoBadge = (i) => {
    if (i.annullata) return <span className="badge bg-danger ms-1">Annullata</span>;
    if (i.rinviata) return <span className="badge bg-warning text-dark ms-1">Rinviata</span>;
    if (i.stato) return <span className={`badge ms-1 ${i.stato === 'Attiva' ? 'bg-success' : 'bg-secondary'}`}>{i.stato}</span>;
    return null;
  };

  return (
    <div className="card mb-4">
      <div className="card-header">
        <div className="d-flex justify-content-between align-items-center">
          <h5 className="mb-0 text-primary">
            <i className="fas fa-calendar-check me-2"></i> Iniziative (Middleware ANUTEL)
          </h5>
          <div className="d-flex align-items-center gap-2">
            {selectedId && (
              <span className="badge bg-success">
                <i className="fas fa-check me-1"></i>Selezionata
              </span>
            )}
            <span className="badge bg-info">
              <i className="fas fa-list me-1"></i>{filteredIniziative.length} elementi
            </span>
            <span className="badge bg-primary">
              Totale: {iniziative.length}
            </span>
          </div>
        </div>
      </div>

      <div className="card-body">
        {error && (
          <div className="alert alert-danger">
            <i className="fas fa-exclamation-triangle me-2"></i>{error}
          </div>
        )}

        {/* Filtri */}
        <div className="row mb-3">
          <div className="col-md-4">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-heading me-1 text-muted"></i> Titolo:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filtra per titolo..."
              value={searchTitolo}
              onChange={e => setSearchTitolo(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-map-marker-alt me-1 text-muted"></i> Luogo:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filtra per luogo..."
              value={searchLuogo}
              onChange={e => setSearchLuogo(e.target.value)}
            />
          </div>
          <div className="col-md-4">
            <label className="form-label fw-bold text-start w-100">
              <i className="fas fa-tag me-1 text-muted"></i> Categoria:
            </label>
            <input
              type="text"
              className="form-control form-control-sm"
              placeholder="Filtra per categoria..."
              value={searchCategoria}
              onChange={e => setSearchCategoria(e.target.value)}
            />
          </div>
        </div>

        {/* Tabella */}
        <div className="table-responsive position-relative" style={{ maxHeight: 420, overflowY: 'auto' }}>
          {(loading || processingSelection) && (
            <div className="position-absolute top-0 start-0 w-100 h-100 d-flex flex-column justify-content-center align-items-center bg-white bg-opacity-75" style={{ zIndex: 10 }}>
              <i className="fas fa-spinner fa-spin fa-3x text-primary mb-3"></i>
              <div className="fw-bold fs-5">
                {loading ? 'Caricamento iniziative...' : 'Aggiornamento...'}
              </div>
            </div>
          )}

          <table className="table table-striped table-hover mb-0">
            <thead className="table-dark sticky-top">
              <tr>
                <th style={{ cursor: 'pointer', minWidth: 220 }} onClick={() => handleSort('titolo')}>
                  Titolo {getSortIcon('titolo')}
                </th>
                <th style={{ cursor: 'pointer', minWidth: 100 }} onClick={() => handleSort('dataDa')}>
                  Data dal {getSortIcon('dataDa')}
                </th>
                <th style={{ cursor: 'pointer', minWidth: 100 }} onClick={() => handleSort('dataA')}>
                  Data a {getSortIcon('dataA')}
                </th>
                <th style={{ cursor: 'pointer', minWidth: 130 }} onClick={() => handleSort('luogo')}>
                  Luogo {getSortIcon('luogo')}
                </th>
                <th style={{ cursor: 'pointer', minWidth: 110 }} onClick={() => handleSort('categoria')}>
                  Categoria {getSortIcon('categoria')}
                </th>
                <th style={{ cursor: 'pointer', minWidth: 100 }} onClick={() => handleSort('erogazione')}>
                  Erogazione {getSortIcon('erogazione')}
                </th>
                <th style={{ cursor: 'pointer', minWidth: 110 }} onClick={() => handleSort('modalitaErogazione')}>
                  Modalità {getSortIcon('modalitaErogazione')}
                </th>
              </tr>
            </thead>
            <tbody>
              {filteredIniziative.length > 0 ? filteredIniziative.map(i => (
                <tr
                  key={i.id}
                  style={{ cursor: 'pointer' }}
                  onClick={() => handleSelect(i.id, i)}
                  className={selectedId === i.id ? 'table-primary' : ''}
                >
                  <td>
                    <div className="d-flex align-items-start gap-1">
                      {selectedId === i.id && <i className="fas fa-check-circle text-success mt-1 flex-shrink-0"></i>}
                      <div>
                        <div className="fw-bold">{i.titolo}</div>
                        {getStatoBadge(i)}
                        {i.nPrenotazioniMax > 0 && (
                          <small className="text-muted d-block mt-1">
                            <i className="fas fa-users me-1"></i>
                            {i.nPrenotatiAttuali}/{i.nPrenotazioniMax} iscritti
                            {i.postiDisponibili > 0 && <span className="text-success ms-1">({i.postiDisponibili} disponibili)</span>}
                          </small>
                        )}
                      </div>
                    </div>
                  </td>
                  <td>
                    <span className="text-nowrap">
                      <i className="fas fa-calendar me-1 text-muted"></i>
                      {formatDate(i.dataDa)}
                    </span>
                  </td>
                  <td>
                    <span className="text-nowrap">
                      <i className="fas fa-calendar-alt me-1 text-muted"></i>
                      {formatDate(i.dataA)}
                    </span>
                  </td>
                  <td>
                    {i.luogo && (
                      <span className="badge bg-info">
                        <i className="fas fa-map-marker-alt me-1"></i>{i.luogo}
                      </span>
                    )}
                  </td>
                  <td>
                    {i.categoria && <span className="badge bg-secondary">{i.categoria}</span>}
                  </td>
                  <td>
                    <small>{i.erogazione}</small>
                  </td>
                  <td>
                    {i.modalitaErogazione && (
                      <span className={`badge ${i.modalitaErogazione === 'In Presenza' ? 'bg-primary' : 'bg-warning text-dark'}`}>
                        {i.modalitaErogazione}
                      </span>
                    )}
                  </td>
                </tr>
              )) : (
                <tr>
                  <td colSpan={7} className="text-center text-muted py-4">
                    <i className="fas fa-calendar-times fa-2x mb-2"></i><br />
                    {loading ? 'Caricamento in corso...' : 'Nessuna iniziativa trovata'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {filteredIniziative.length > 0 && (
          <div className="d-flex justify-content-between align-items-center mt-2">
            <small className="text-muted">
              <i className="fas fa-info-circle me-1"></i>
              Visualizzate {filteredIniziative.length} di {iniziative.length} iniziative
            </small>
            {selectedId && (
              <small className="text-success">
                <i className="fas fa-check-circle me-1"></i>
                Iniziativa selezionata — ID: {selectedId}
              </small>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default ContattiPrenotazioneNuovo;
